import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, LogOut, RefreshCw, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass =
  "w-full bg-white border border-stone-200 rounded-none px-4 py-3 text-sm font-light text-stone-800 focus:border-terracotta-600 focus:outline-none transition-colors duration-300";
const labelClass = "block text-xs font-semibold tracking-[0.15em] uppercase text-stone-500 mb-2";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Qualcosa è andato storto. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function Field({ label, value, onChange, textarea = false, type = "text", testid }) {
  return (
    <div>
      <label className={labelClass}>{label}</label>
      {textarea ? (
        <textarea rows={3} data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className={`${inputClass} resize-y`} />
      ) : (
        <input type={type} data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className={inputClass} />
      )}
    </div>
  );
}

function ImageField({ label, value, onChange, token, testid }) {
  const [uploading, setUploading] = useState(false);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onChange(`${process.env.REACT_APP_BACKEND_URL}${data.url}`);
      toast.success("Foto caricata. Premi Salva per pubblicarla.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div>
      <label className={labelClass}>{label}</label>
      <div className="flex items-start gap-4">
        {value && (
          <img src={value} alt="" className="w-28 h-20 object-cover border border-stone-200 shrink-0" />
        )}
        <div className="flex-1 space-y-3">
          <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className={inputClass} />
          <label
            className={`inline-flex items-center gap-2 px-5 py-2.5 border border-olive-900 text-olive-900 text-xs font-semibold tracking-[0.15em] uppercase transition-colors duration-300 hover:bg-olive-900 hover:text-sand-50 ${uploading ? "opacity-60 pointer-events-none" : "cursor-pointer"}`}
          >
            {uploading ? "Caricamento…" : "Carica foto dal computer"}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              className="hidden"
              data-testid={`${testid}-upload`}
              onChange={pick}
              disabled={uploading}
            />
          </label>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="border border-stone-200 bg-sand-50 p-6 lg:p-8">
      <h2 className="font-serif text-2xl text-olive-900 mb-6">{title}</h2>
      <div className="space-y-5">{children}</div>
    </section>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("admin_token") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [changingPwd, setChangingPwd] = useState(false);
  const [requests, setRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);

  useEffect(() => {
    if (!token) return;
    const load = async () => {
      try {
        await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
        const { data } = await axios.get(`${API}/content`);
        setContent(data);
        loadRequests();
      } catch {
        localStorage.removeItem("admin_token");
        setToken("");
      }
    };
    load();
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoggingIn(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("admin_token", data.access_token);
      setToken(data.access_token);
    } catch (err) {
      setLoginError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("admin_token");
    setToken("");
    setContent(null);
  };

  const loadRequests = async () => {
    setLoadingRequests(true);
    try {
      const { data } = await axios.get(`${API}/admin/preventivi`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRequests(data);
    } catch {
      // token non ancora pronto o errore di rete: la lista resta vuota
    } finally {
      setLoadingRequests(false);
    }
  };

  const deleteRequest = async (id) => {
    try {
      await axios.delete(`${API}/admin/preventivi/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRequests((r) => r.filter((x) => x.id !== id));
      toast.success("Richiesta eliminata.");
    } catch {
      toast.error("Eliminazione non riuscita.");
    }
  };

  const GUEST_LABELS = { "15-20": "15 – 20", "21-30": "21 – 30", "31-50": "31 – 50", "oltre-50": "Oltre 50" };
  const formatDate = (iso) =>
    iso ? new Date(iso).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" }) : "—";

  const setPath = (path, value) => {
    setContent((c) => {
      const copy = structuredClone(c);
      let obj = copy;
      for (let i = 0; i < path.length - 1; i++) obj = obj[path[i]];
      obj[path[path.length - 1]] = value;
      return copy;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/content`, content, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Contenuti salvati. Il sito è già aggiornato.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwd.next !== pwd.confirm) {
      toast.error("Le nuove password non coincidono.");
      return;
    }
    setChangingPwd(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { current_password: pwd.current, new_password: pwd.next },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Password aggiornata con successo.");
      setPwd({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setChangingPwd(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-sand-50 flex items-center justify-center px-6">
        <div className="w-full max-w-md border border-stone-200 bg-white p-8 lg:p-10">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-terracotta-600 mb-3">Area riservata</p>
          <h1 className="font-serif text-3xl text-olive-900">Pannello di gestione</h1>
          <form data-testid="admin-login-form" onSubmit={login} className="mt-8 space-y-5">
            <div>
              <label htmlFor="admin-email" className={labelClass}>Email</label>
              <input
                id="admin-email"
                type="email"
                required
                data-testid="admin-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="admin-password" className={labelClass}>Password</label>
              <input
                id="admin-password"
                type="password"
                required
                data-testid="admin-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputClass}
              />
            </div>
            {loginError && (
              <p data-testid="admin-login-error" className="text-sm text-terracotta-600">{loginError}</p>
            )}
            <button
              type="submit"
              data-testid="admin-login-button"
              disabled={loggingIn}
              className="w-full px-8 py-4 bg-olive-900 text-sand-50 text-xs font-semibold tracking-[0.18em] uppercase transition-colors duration-300 hover:bg-olive-800 disabled:opacity-60"
            >
              {loggingIn ? "Accesso in corso…" : "Accedi"}
            </button>
          </form>
          <Link to="/" data-testid="admin-back-home" className="mt-6 inline-flex items-center gap-2 text-xs font-medium tracking-[0.15em] uppercase text-stone-500 hover:text-terracotta-600 transition-colors duration-300">
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Torna al sito
          </Link>
        </div>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="min-h-screen bg-sand-50 flex items-center justify-center">
        <p className="text-sm font-light text-stone-500">Caricamento contenuti…</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-panel" className="min-h-screen bg-sand-100">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-sand-50/90 border-b border-stone-200">
        <div className="max-w-5xl mx-auto px-6 h-20 flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-terracotta-600">Area riservata</p>
            <h1 className="font-serif text-2xl text-olive-900 leading-none">Pannello di gestione</h1>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="admin-view-site" className="hidden sm:inline-flex px-5 py-3 border border-olive-900 text-olive-900 text-xs font-semibold tracking-[0.15em] uppercase transition-colors duration-300 hover:bg-olive-900 hover:text-sand-50">
              Vedi il sito
            </Link>
            <button
              data-testid="admin-save-button"
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-2 px-6 py-3 bg-olive-900 text-sand-50 text-xs font-semibold tracking-[0.15em] uppercase transition-colors duration-300 hover:bg-olive-800 disabled:opacity-60"
            >
              <Save className="w-4 h-4" strokeWidth={1.5} />
              {saving ? "Salvataggio…" : "Salva"}
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              aria-label="Esci"
              className="p-3 border border-stone-200 text-stone-500 transition-colors duration-300 hover:text-terracotta-600 hover:border-terracotta-600"
            >
              <LogOut className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12 space-y-8">
        <Section title="Richieste di preventivo">
          <div className="flex items-center justify-between gap-4">
            <p className="text-sm font-light text-stone-500" data-testid="admin-requests-count">
              {loadingRequests ? "Caricamento…" : `${requests.length} ${requests.length === 1 ? "richiesta ricevuta" : "richieste ricevute"}`}
            </p>
            <button
              data-testid="admin-refresh-requests"
              onClick={loadRequests}
              className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.15em] uppercase text-olive-900 hover:text-terracotta-600 transition-colors duration-300"
            >
              <RefreshCw className="w-4 h-4" strokeWidth={1.5} /> Aggiorna
            </button>
          </div>
          {requests.length === 0 && !loadingRequests && (
            <p className="text-sm font-light text-stone-500 border border-dashed border-stone-200 p-6 text-center">
              Nessuna richiesta per ora. Le nuove richieste arrivano anche via email.
            </p>
          )}
          <div className="space-y-4">
            {requests.map((r) => (
              <div key={r.id} data-testid={`admin-request-${r.id}`} className="border border-stone-200 bg-white p-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="font-serif text-xl text-olive-900">{r.name}</p>
                    <p className="text-xs font-light text-stone-400 mt-1">Ricevuta il {formatDate(r.created_at)}</p>
                  </div>
                  <button
                    data-testid={`admin-delete-request-${r.id}`}
                    onClick={() => deleteRequest(r.id)}
                    aria-label="Elimina richiesta"
                    className="p-2 text-stone-400 hover:text-terracotta-600 transition-colors duration-300"
                  >
                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </div>
                <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm font-light text-stone-600">
                  <span><span className="text-stone-400">Email:</span> {r.email}</span>
                  <span><span className="text-stone-400">Telefono:</span> {r.phone}</span>
                  <span><span className="text-stone-400">Ospiti:</span> {GUEST_LABELS[r.guests] || r.guests} persone</span>
                  <span><span className="text-stone-400">Data evento:</span> {r.event_date || "Da definire"}</span>
                </div>
                {r.message && (
                  <p className="mt-3 text-sm font-light text-stone-600 border-t border-stone-200 pt-3">{r.message}</p>
                )}
              </div>
            ))}
          </div>
        </Section>

        <Section title="Contatti">
          <Field label="Nome del locale" testid="admin-field-site-name" value={content.site.name} onChange={(v) => setPath(["site", "name"], v)} />
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Telefono" testid="admin-field-site-phone" value={content.site.phone} onChange={(v) => setPath(["site", "phone"], v)} />
            <Field label="Email" testid="admin-field-site-email" value={content.site.email} onChange={(v) => setPath(["site", "email"], v)} />
          </div>
          <Field label="Indirizzo" testid="admin-field-site-address" value={content.site.address} onChange={(v) => setPath(["site", "address"], v)} />
        </Section>

        <Section title="Home — prima schermata">
          <Field label="Scritta sopra il nome" testid="admin-field-hero-overline" value={content.hero.overline} onChange={(v) => setPath(["hero", "overline"], v)} />
          <Field label="Sottotitolo" textarea testid="admin-field-hero-subtitle" value={content.hero.subtitle} onChange={(v) => setPath(["hero", "subtitle"], v)} />
          <ImageField label="Foto principale" testid="admin-field-images-hero" token={token} value={content.images.hero} onChange={(v) => setPath(["images", "hero"], v)} />
        </Section>

        <Section title="La Trattoria — storia">
          <Field label="Titolo" testid="admin-field-storia-heading" value={content.storia.heading} onChange={(v) => setPath(["storia", "heading"], v)} />
          <Field label="Primo paragrafo" textarea testid="admin-field-storia-p1" value={content.storia.p1} onChange={(v) => setPath(["storia", "p1"], v)} />
          <Field label="Secondo paragrafo" textarea testid="admin-field-storia-p2" value={content.storia.p2} onChange={(v) => setPath(["storia", "p2"], v)} />
          <ImageField label="Foto sala" testid="admin-field-images-interior" token={token} value={content.images.interior} onChange={(v) => setPath(["images", "interior"], v)} />
          <ImageField label="Foto cantina" testid="admin-field-images-cellar" token={token} value={content.images.cellar} onChange={(v) => setPath(["images", "cellar"], v)} />
          {content.storia.features.map((f, i) => (
            <div key={i} className="grid sm:grid-cols-2 gap-5 pt-4 border-t border-stone-200">
              <Field label={`Riquadro ${i + 1} — titolo`} testid={`admin-field-feature-${i}-title`} value={f.title} onChange={(v) => setPath(["storia", "features", i, "title"], v)} />
              <Field label={`Riquadro ${i + 1} — testo`} testid={`admin-field-feature-${i}-text`} value={f.text} onChange={(v) => setPath(["storia", "features", i, "text"], v)} />
            </div>
          ))}
        </Section>

        <Section title="Galleria">
          <Field label="Titolo sezione" testid="admin-field-galleria-heading" value={content.galleria.heading} onChange={(v) => setPath(["galleria", "heading"], v)} />
          {content.galleria.items.map((item, i) => (
            <div key={i} className="space-y-4 pt-4 border-t border-stone-200">
              <ImageField label={`Foto ${i + 1}`} testid={`admin-field-gallery-${i}-src`} token={token} value={item.src} onChange={(v) => setPath(["galleria", "items", i, "src"], v)} />
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label={`Foto ${i + 1} — didascalia`} testid={`admin-field-gallery-${i}-caption`} value={item.caption} onChange={(v) => setPath(["galleria", "items", i, "caption"], v)} />
                <Field label={`Foto ${i + 1} — testo alternativo`} testid={`admin-field-gallery-${i}-alt`} value={item.alt} onChange={(v) => setPath(["galleria", "items", i, "alt"], v)} />
              </div>
            </div>
          ))}
        </Section>

        <Section title="Orari di apertura">
          {content.orari.rows.map((row, i) => (
            <div key={i} className="grid sm:grid-cols-2 gap-5">
              <Field label={`Riga ${i + 1} — giorni`} testid={`admin-field-orari-${i}-giorni`} value={row.giorni} onChange={(v) => setPath(["orari", "rows", i, "giorni"], v)} />
              <Field label={`Riga ${i + 1} — orari`} testid={`admin-field-orari-${i}-orari`} value={row.orari} onChange={(v) => setPath(["orari", "rows", i, "orari"], v)} />
            </div>
          ))}
          <Field label="Nota sotto gli orari" testid="admin-field-orari-note" value={content.orari.note} onChange={(v) => setPath(["orari", "note"], v)} />
        </Section>

        <Section title="Preventivi per gruppi">
          <Field label="Titolo sezione" testid="admin-field-preventivi-heading" value={content.preventivi.heading} onChange={(v) => setPath(["preventivi", "heading"], v)} />
          <Field label="Testo introduttivo" textarea testid="admin-field-preventivi-intro" value={content.preventivi.intro} onChange={(v) => setPath(["preventivi", "intro"], v)} />
          <Field label="Avviso accanto al modulo" textarea testid="admin-field-preventivi-disclaimer" value={content.preventivi.disclaimer} onChange={(v) => setPath(["preventivi", "disclaimer"], v)} />
        </Section>

        <Section title="Piè di pagina">
          <Field label="Frase sotto il nome" testid="admin-field-footer-tagline" value={content.footer.tagline} onChange={(v) => setPath(["footer", "tagline"], v)} />
          <Field label="Riga finale" testid="admin-field-footer-bottom" value={content.footer.bottom} onChange={(v) => setPath(["footer", "bottom"], v)} />
        </Section>

        <Section title="Sicurezza — cambia password">
          <form data-testid="admin-password-form" onSubmit={changePassword} className="space-y-5">
            <Field label="Password attuale" type="password" testid="admin-current-password-input" value={pwd.current} onChange={(v) => setPwd((p) => ({ ...p, current: v }))} />
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Nuova password (min. 8 caratteri)" type="password" testid="admin-new-password-input" value={pwd.next} onChange={(v) => setPwd((p) => ({ ...p, next: v }))} />
              <Field label="Conferma nuova password" type="password" testid="admin-confirm-password-input" value={pwd.confirm} onChange={(v) => setPwd((p) => ({ ...p, confirm: v }))} />
            </div>
            <button
              type="submit"
              data-testid="admin-change-password-button"
              disabled={changingPwd || !pwd.current || !pwd.next}
              className="px-8 py-3.5 bg-olive-900 text-sand-50 text-xs font-semibold tracking-[0.15em] uppercase transition-colors duration-300 hover:bg-olive-800 disabled:opacity-60"
            >
              {changingPwd ? "Aggiornamento…" : "Cambia password"}
            </button>
          </form>
        </Section>
      </main>
    </div>
  );
}
